<template>
  <div style="padding: 20px;">
    <LoaderView v-if="shoppingList == null" />

    <div v-else>

      <div class="form-title">Mes repas pour les 7 prochains jours</div>

      <div class="menu-item-container">
        <div v-for="(dateContent, date) in shoppingList.menu_items" :key="date"
          style="display: flex; flex-direction: column; gap: 10px;">
          <strong>{{ humanizeDate(date) }}</strong>
          <div v-for="(menuItems, menuType) in dateContent" :key="menuType">
            <BigToggleButton v-for="(menuItem, index) in menuItems" :key="index"
              :value="menuItem.include_in_shopping_list" @input="toggleMenuItem(menuItem)" style="max-width: 200px;">
              <div class="menu-item-meal">
                {{ humanizeMenuType(menuType) }}
              </div>
              {{ menuItem.name }}
            </BigToggleButton>
          </div>
        </div>
      </div>

      <div style="margin-top: 30px; display: flex; justify-content: space-between; align-items: center;">
        <div class="form-title">
          Ma liste de course
        </div>
        <button class="button" @click="downloadShoppingList">
          <font-awesome-icon class="icon" :icon="['fas', 'download']" />
          Télécharger la liste des produits cochés
        </button>
      </div>

      <div class="ingredient-container">
        <div class="card" v-for="(ingredients, category) in ingredientsByCategory" :key="category">
          <div><strong>Rayon {{ category }}</strong></div>
          <div v-for="ingredient in ingredients" :key="ingredient.id">
            <input type="checkbox" :checked="ingredient.include_in_shopping_list" @change="toggleAliment(ingredient)" />
            {{ ingredient.name }} x {{ ingredient.quantity }} {{ ingredient.unit }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { api } from "@/api";
import BigToggleButton from "@/components/BigToggleButton.vue";
import jsPDF from "jspdf";
import LoaderView from "@/components/LoaderView.vue";

export default {
  name: "MyShoppingList",
  components: {
    BigToggleButton,
    LoaderView,
  },
  data() {
    return {
      shoppingList: null,
    };
  },
  computed: {
    ingredientsByCategory() {
      if (this.shoppingList == null) {
        return [];
      }

      const ingredients = this.shoppingList.ingredients;

      const ingredientsByCategory = {};

      for (const ingredient of ingredients) {
        if (ingredientsByCategory[ingredient.category] == null) {
          ingredientsByCategory[ingredient.category] = [];
        }

        ingredientsByCategory[ingredient.category].push(ingredient);
      }

      return ingredientsByCategory;
    },
  },
  mounted() {
    this.loadShoppingList();
  },
  methods: {
    loadShoppingList() {
      api.get("my-shopping-list/").then((response) => {
        this.shoppingList = response.data;
      });
    },

    toggleMenuItem(menuItem) {
      // Toggle the menu item in the shopping list

      menuItem.include_in_shopping_list = !menuItem.include_in_shopping_list;

      api.post(`/my-shopping-list/${menuItem.id}/toggle/`).then((response) => {
        this.shoppingList.ingredients = response.data.ingredients;
      });
    },
    toggleAliment(aliment) {
      // Toggle the menu item in the shopping list

      api.post(`my-shopping-list/aliment/${aliment.id}/toggle/`).then((response) => {
        this.shoppingList.ingredients = response.data.ingredients;
      });
    },
    async downloadShoppingList() {
      const doc = new jsPDF();

      const logoWidth = 50;
      const logoHeight = 20;
      const pageWidth = doc.internal.pageSize.getWidth();
      const logoX = pageWidth - logoWidth - 20;
      const logoY = 10;
      const logoUrl = '/logo.png';
      const imageData = await this.getBase64Image(logoUrl);

      // ✅ HEADER : logo + titre
      const drawHeader = () => {
        doc.addImage(imageData, 'PNG', logoX, logoY, logoWidth, logoHeight);
        doc.setFontSize(18);
        doc.text("Ma Liste de Courses", 20, 26);
        doc.setLineWidth(0.5);
        doc.line(20, 30, pageWidth - 20, 30);
      };

      drawHeader();

      // --- Layout ---
      let yOffset = 40;
      const marginLeft = 20;
      const columnGap = 10;
      const columnWidth = (pageWidth - marginLeft * 2 - columnGap) / 2;
      const lineHeight = 6;
      let xOffset = marginLeft;

      // ✅ Police par défaut
      doc.setFontSize(10);

      for (const category in this.ingredientsByCategory) {
        // Nouvelle page si trop bas
        if (yOffset > 270) {
          if (xOffset === marginLeft) {
            xOffset += columnWidth + columnGap;
            yOffset = 40;
          } else {
            doc.addPage();
            xOffset = marginLeft;
            yOffset = 40;
            // ✅ Réapplique le header et la taille de police correcte
            drawHeader();
            doc.setFontSize(10);
          }
        }

        // ✅ Catégorie
        doc.setFontSize(11);
        doc.text(`Rayon ${category}`, xOffset, yOffset);
        yOffset += lineHeight;

        // ✅ Ingrédients
        doc.setFontSize(9);
        for (const ingredient of this.ingredientsByCategory[category]) {
          if (!ingredient.include_in_shopping_list) continue;

          if (yOffset > 270) {
            if (xOffset === marginLeft) {
              xOffset += columnWidth + columnGap;
              yOffset = 40;
            } else {
              doc.addPage();
              xOffset = marginLeft;
              yOffset = 40;
              // ✅ Réapplique le header et la taille du texte ici aussi
              drawHeader();
              doc.setFontSize(10);
            }
          }

          const line = `${ingredient.name} x ${ingredient.quantity} ${ingredient.unit}`;

          // Découpe automatique du texte à la largeur de la colonne
          const wrappedText = doc.splitTextToSize(line, columnWidth - 4); // marge intérieure légère

          for (const txtLine of wrappedText) {
            if (yOffset > 270) {
              if (xOffset === marginLeft) {
                xOffset += columnWidth + columnGap;
                yOffset = 40;
              } else {
                doc.addPage();
                xOffset = marginLeft;
                yOffset = 40;
                drawHeader();
                doc.setFontSize(10);
              }
            }
            doc.text(txtLine, xOffset, yOffset);
            yOffset += lineHeight;
          }
        }

        yOffset += lineHeight;
      }

      doc.save("liste-de-course.pdf");
    },
    humanizeDate(isoDate) {
      const date = new Date(isoDate);
      const res = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
      // Capitalize the first letter
      return res.charAt(0).toUpperCase() + res.slice(1);
    },
    humanizeMenuType(menuType) {
      switch (menuType) {
        case "breakfast":
          return "Petit-déjeuner";
        case "lunch_starter":
          return "Entrée du déjeuner";
        case "lunch_main":
          return "Plat principal du déjeuner";
        case "lunch_dessert":
          return "Dessert du déjeuner";
        case "diner_starter":
          return "Entrée du dîner";
        case "diner_main":
          return "Plat principal du dîner";
        case "diner_dessert":
          return "Dessert du dîner";
        case "snack":
          return "En-cas";
        default:
          return menuType;
      }
    },
    getBase64Image(url) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.setAttribute("crossOrigin", "anonymous");
        img.onload = () => {
          const canvas = document.createElement("canvas");
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
      });
    }
  },
};



</script>


<style scoped>
.menu-item-container {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}


.ingredient-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-item-meal {
  font-size: 20px;
  font-weight: 800;
  color: #436f0e;
}
</style>
